import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Shared Server-Sent Events (SSE) hook with reconnection logic.
 *
 * This version lives in sipli-fleet-core and is UI-/app-agnostic.
 * Consumers are responsible for providing a `getToken` function if
 * authentication is required.
 *
 * @param {Function} getStreamUrl - Function that returns the stream URL
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable the stream
 * @param {number} options.reconnectDelay - Delay before reconnecting (ms)
 * @param {number} options.maxReconnectAttempts - Maximum reconnection attempts
 * @param {Function} options.onMessage - Callback when message received
 * @param {Function} options.onError - Callback when error occurs
 * @param {Function} options.getToken - Function that returns auth token (optional)
 *
 * @returns {Object} { data, setData, isLoading, error, reconnectAttempts }
 */
const useServerSentEvents = (
  getStreamUrl,
  {
    enabled = true,
    reconnectDelay = 3000,
    maxReconnectAttempts = 5,
    onMessage,
    onError,
    getToken,
  } = {},
) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const abortControllerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);

  // Store functions in refs to avoid dependency issues
  // This prevents the effect from re-running when function references change
  const getStreamUrlRef = useRef(getStreamUrl);
  const getTokenRef = useRef(getToken);
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);

  // Keep refs updated with latest function references
  useEffect(() => {
    getStreamUrlRef.current = getStreamUrl;
    getTokenRef.current = getToken;
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const token =
      typeof getTokenRef.current === "function" ? getTokenRef.current() : null;
    const streamUrl = getStreamUrlRef.current();

    if (!streamUrl || !token) {
      return undefined;
    }

    const cleanupExisting = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const connect = () => {
      if (isUnmountedRef.current) return;

      // Get fresh token and URL for each connection attempt
      const currentToken =
        typeof getTokenRef.current === "function"
          ? getTokenRef.current()
          : null;
      const currentStreamUrl = getStreamUrlRef.current();

      if (!currentStreamUrl || !currentToken) {
        return;
      }

      setIsLoading(true);
      setError(null);

      cleanupExisting();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Handle incoming SSE messages
      const handleMessage = (rawData) => {
        try {
          const payload = JSON.parse(rawData ?? "{}");

          // Check if payload is array (historical) or object (new item)
          if (Array.isArray(payload)) {
            setData((prev) => {
              // Initial load: we can safely set the full list,
              // but if server sends an empty array we keep an
              // already-populated list to avoid a visible "jerk".
              if (isInitialLoadRef.current) {
                isInitialLoadRef.current = false;
                if (payload.length === 0 && prev.length > 0) {
                  return prev;
                }
                return payload;
              }

              // Subsequent array updates: merge intelligently by id.
              if (prev.length > 0) {
                const existingIds = new Set(prev.map((item) => item.id));
                const newItems = payload.filter(
                  (item) => !existingIds.has(item.id),
                );
                // If there are new items, add them; otherwise keep existing data
                return newItems.length > 0 ? [...newItems, ...prev] : prev;
              }

              // If no existing data yet, use the new payload as-is
              return payload;
            });
          } else if (payload.id) {
            // New single notification - add to beginning of list
            setData((prev) => {
              // Prevent duplicates
              if (prev.some((item) => item.id === payload.id)) {
                return prev;
              }
              return [payload, ...prev];
            });
            isInitialLoadRef.current = false;
          }

          // Call custom onMessage handler if provided
          if (onMessageRef.current) {
            onMessageRef.current(payload);
          }

          // Reset reconnect attempts on successful message
          reconnectAttemptsRef.current = 0;
          setReconnectAttempts(0);
        } catch (err) {
          setError(err);
          if (onErrorRef.current) {
            onErrorRef.current(err);
          }
        }
      };

      const openFetchStream = async () => {
        try {
          const response = await fetch(currentStreamUrl, {
            headers: {
              ...(currentToken
                ? { Authorization: `Bearer ${currentToken}` }
                : {}),
            },
            signal: controller.signal,
          });

          if (!response.ok) {
            throw new Error(`Stream error: ${response.status}`);
          }

          setIsLoading(false);
          const reader = response.body?.getReader();
          if (!reader) throw new Error("Stream reader unavailable");

          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const events = buffer.split("\n\n");
            buffer = events.pop() ?? "";

            events.forEach((eventChunk) => {
              const dataLines = eventChunk
                .split(/\r?\n/)
                .filter((line) => line.startsWith("data:"))
                .map((line) => line.replace(/^data:\s?/, ""));

              if (!dataLines.length) return;
              handleMessage(dataLines.join("\n"));
            });
          }

          // Stream ended normally, attempt reconnect
          if (
            !isUnmountedRef.current &&
            reconnectAttemptsRef.current < maxReconnectAttempts
          ) {
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current += 1;
              setReconnectAttempts(reconnectAttemptsRef.current);
              connect();
            }, reconnectDelay);
          }
        } catch (err) {
          if (controller.signal.aborted || isUnmountedRef.current) return;

          setError(err);
          setIsLoading(false);

          if (onErrorRef.current) {
            onErrorRef.current(err);
          }

          // Attempt reconnect if not at max attempts
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current += 1;
              setReconnectAttempts(reconnectAttemptsRef.current);
              connect();
            }, reconnectDelay);
          }
        }
      };

      openFetchStream();
    };

    // Initial connection
    connect();

    // Cleanup on unmount or when enabled changes
    return () => {
      cleanupExisting();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // Only re-run when `enabled`, `reconnectDelay`, or `maxReconnectAttempts` change
    // Functions are stored in refs to avoid triggering re-connections
  }, [enabled, reconnectDelay, maxReconnectAttempts]);

  return { data, setData, isLoading, error, reconnectAttempts };
};

export default useServerSentEvents;

